import { isDerivedStillImage } from '@/types/asset-hierarchy'
import type { Asset, AssetType } from '../types'

export type AssetMediaKindInput = {
  assetType?: AssetType | string | null
  mimeType?: string | null
  operation?: Asset['operation'] | string | null
}

export type DownloadAnalyticsAssetType = Exclude<AssetType, 'set' | 'file'>

/**
 * Canonical frontend media-kind resolver for legacy and current asset records.
 * Explicit assetType wins. For legacy set drafts, i2s must beat image MIME
 * because image-to-set vessels can carry image/png source previews.
 */
export function getAssetMediaKind(asset: AssetMediaKindInput): AssetType {
  switch (asset.assetType) {
    case 'video':
    case 'audio':
    case 'image':
    case 'file':
    case 'set':
      return asset.assetType
  }

  if (asset.mimeType === 'application/json') return 'file'
  if (asset.operation === 'i2s') return 'set'

  if (asset.mimeType?.startsWith('image/')) return 'image'
  if (asset.mimeType?.startsWith('audio/')) return 'audio'
  if (asset.mimeType?.startsWith('video/')) return 'video'

  if (isDerivedStillImage(asset.operation)) return 'image'

  switch (asset.operation) {
    case 'generate-image':
    case 'video-frame':
    case 'set-screenshot':
      return 'image'
    case 'generate-audio':
    case 'inpaint-audio':
      return 'audio'
    case 't2v':
    case 'i2v':
    case 'extend':
    case 'variation':
    case 'offshoot':
    case 'v2v':
    case 'upload':
    case 'upscale':
    case 'camera-motion':
      return 'video'
  }

  return 'video'
}

/**
 * Resolves the documented download analytics domain. Set previews do not
 * expose downloads, so callers should stop when this returns null.
 */
export function getDownloadAnalyticsAssetType(asset: AssetMediaKindInput): DownloadAnalyticsAssetType | null {
  const mediaKind = getAssetMediaKind(asset)
  return mediaKind === 'set' || mediaKind === 'file' ? null : mediaKind
}
